const SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY;

let scriptLoadedPromise = null;

/**
 * Dynamically loads the Google reCAPTCHA v3 script if not already present
 * @returns {Promise<any>} Resolves with window.grecaptcha
 */
export const loadRecaptchaScript = () => {
	if (typeof window === 'undefined') {
		return Promise.reject(new Error('Window object is not available'));
	}

	if (!SITE_KEY) {
		return Promise.resolve(null);
	}

	if (window.grecaptcha && window.grecaptcha.execute) {
		return Promise.resolve(window.grecaptcha);
	}

	if (!scriptLoadedPromise) {
		scriptLoadedPromise = new Promise((resolve, reject) => {
			const existingScript = document.querySelector('script[src*="recaptcha/api.js"]');
			if (existingScript) {
				if (window.grecaptcha && window.grecaptcha.execute) {
					return resolve(window.grecaptcha);
				}
				if (window.grecaptcha && window.grecaptcha.ready) {
					window.grecaptcha.ready(() => resolve(window.grecaptcha));
					return;
				}
				existingScript.addEventListener('load', () => {
					if (window.grecaptcha) {
						window.grecaptcha.ready(() => resolve(window.grecaptcha));
					} else {
						reject(new Error('Google reCAPTCHA failed to initialize'));
					}
				});
				existingScript.addEventListener('error', () => {
					existingScript.remove();
					reject(new Error('Failed to load Google reCAPTCHA script'));
				});
				return;
			}

			const script = document.createElement('script');
			script.src = `https://www.google.com/recaptcha/api.js?render=${SITE_KEY}`;
			script.async = true;
			script.defer = true;
			script.onload = () => {
				if (window.grecaptcha) {
					window.grecaptcha.ready(() => resolve(window.grecaptcha));
				} else {
					reject(new Error('Google reCAPTCHA failed to initialize'));
				}
			};
			script.onerror = () => {
				script.remove();
				reject(new Error('Failed to load Google reCAPTCHA script'));
			};
			document.head.appendChild(script);
		}).catch(err => {
			scriptLoadedPromise = null;
			throw err;
		});
	}

	return scriptLoadedPromise;
};

/**
 * Execute Google reCAPTCHA v3 and return the verification token
 * @param {string} action - The action name for analytics/verification
 * @returns {Promise<string>}
 */
export const executeRecaptcha = async (action = 'signup') => {
	if (!SITE_KEY) {
		return '';
	}

	try {
		const grecaptcha = await loadRecaptchaScript();
		if (!grecaptcha) {
			return '';
		}

		const token = await new Promise((resolve, reject) => {
			const timeoutId = setTimeout(() => {
				reject(new Error('Security verification timed out. Please try again.'));
			}, 10000);

			grecaptcha.ready(async () => {
				try {
					const res = await grecaptcha.execute(SITE_KEY, { action });
					clearTimeout(timeoutId);
					resolve(res);
				} catch (err) {
					clearTimeout(timeoutId);
					reject(err);
				}
			});
		});

		if (!token || typeof token !== 'string' || !token.trim()) {
			throw new Error(
				'Security verification could not be completed. If you are using an ad blocker or privacy extension, please disable it for this page and try again.'
			);
		}

		return token;
	} catch (error) {
		const message = error?.message || '';
		if (message.includes('ad blocker') || message.includes('timed out')) {
			throw error;
		}
		throw new Error(
			'Security verification failed to initialize. If you are using an ad blocker or privacy shield, please disable it for this page and try again.'
		);
	}
};
